import { ReactNode, FC } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Helmet } from 'react-helmet'
import { Route, Routes, Outlet, useParams, Navigate } from 'react-router-dom'
import EditorSceneLayout from '@editor/components/EditorSceneLayout/index'
import { useQuery } from '@tanstack/react-query'
import { GenericErrorBoundaryFallback } from '@ui/errors'
import PreviewDeploymentWarningDialog from '@ui/feedback/PreviewDeploymentWarningDialog'
import { Layout } from '@ui/structure'
import { ProtectedRoute } from 'src/components/auth'
import { Login, Home, Editor, Manager, PlanViewer, UserManager } from 'src/components/pages'
import {
  ProjectSystemManager,
  IFCImport,
  GlobalLoads,
  StructuralPlanning,
  Results2,
  PlanView,
  Plans,
} from 'src/components/pages/Editor/pages'
import Export from 'src/components/pages/Editor/pages/Export'
// import EditorSceneLayout from './components/pages/Editor/components/EditorSceneLayout'
import DocumentManager from './components/pages/Editor/pages/DocumentManager'
import { appConfig, earthMassForce } from './constants'
import { useAuthToken, useIntercom } from './hooks'
import { getProject } from './state/queries/project'

interface WrapperProps {
  pageName: string
  children: ReactNode
}

const AddPageTitleFromProjectWrapper: FC<WrapperProps> = ({ pageName, children }: WrapperProps) => {
  const { projectId } = useParams()
  const { data: project } = useQuery({
    queryKey: getProject.getKey(projectId),
    queryFn: () => getProject.request(projectId),
  })
  const projectNameFormatted = project?.name || ''

  return (
    <>
      <Helmet>
        <title>{projectNameFormatted}</title>
      </Helmet>
      <div
        className="AddPageTitleFromProjectWrapper"
        key={pageName}
        style={{ height: '100%', minHeight: 0 }}
      >
        <ErrorBoundary FallbackComponent={GenericErrorBoundaryFallback}>{children}</ErrorBoundary>
      </div>
    </>
  )
}

const AuthRequiredRoutes = () => {
  useAuthToken()

  return (
    <Routes>
      <Route path="/authorize" element={<Login />} />
      <Route path="/plans/:projectId/:planId" element={<ProtectedRoute element={<Outlet />} />}>
        <Route index element={<PlanViewer />} />
      </Route>
      <Route
        element={
          <ProtectedRoute
            element={
              <Layout>
                <Outlet />
              </Layout>
            }
          />
        }
      >
        <Route index element={<ProtectedRoute element={<Home />} />} />
        <Route
          path="/manager"
          element={<ProtectedRoute element={<Manager earthMassForce={earthMassForce} />} />}
        />
        <Route path="/user-manager" element={<ProtectedRoute element={<UserManager />} />} />
        <Route path="/editor/:projectId" element={<ProtectedRoute element={<Editor />} />}>
          <Route index element={<Navigate to="manager" replace />} />
          <Route path="manager" element={<ProjectSystemManager />} />
          <Route
            path="/editor/:projectId/assembly-assignment"
            element={
              <AddPageTitleFromProjectWrapper pageName="System Manager">
                <ProjectSystemManager />
              </AddPageTitleFromProjectWrapper>
            }
          />
          <Route element={<EditorSceneLayout />}>
            <Route
              path="ifc-import"
              element={
                <AddPageTitleFromProjectWrapper pageName="IFC Import">
                  <IFCImport />
                </AddPageTitleFromProjectWrapper>
              }
            />
            <Route
              path="global-loads"
              element={
                <AddPageTitleFromProjectWrapper pageName="Einwirkungen">
                  <GlobalLoads />
                </AddPageTitleFromProjectWrapper>
              }
            />
            <Route
              path="structural-planning"
              element={
                <AddPageTitleFromProjectWrapper pageName="Tragwerksplanung">
                  <StructuralPlanning />
                </AddPageTitleFromProjectWrapper>
              }
            />
            <Route
              path="results"
              element={
                <AddPageTitleFromProjectWrapper pageName="Ergebnisse">
                  <Results2 />
                </AddPageTitleFromProjectWrapper>
              }
            />
            <Route
              path="plan-view"
              element={
                <AddPageTitleFromProjectWrapper pageName="Planansicht">
                  <PlanView />
                </AddPageTitleFromProjectWrapper>
              }
            />
            <Route
              path="export"
              element={
                <AddPageTitleFromProjectWrapper pageName="Export">
                  <Export />
                </AddPageTitleFromProjectWrapper>
              }
            />
          </Route>
          <Route
            path="/editor/:projectId/document-manager"
            element={
              <AddPageTitleFromProjectWrapper pageName="PDF">
                <DocumentManager />
              </AddPageTitleFromProjectWrapper>
            }
          />
          <Route
            path="/editor/:projectId/plans"
            element={
              <AddPageTitleFromProjectWrapper pageName="Pläne">
                <Plans />
              </AddPageTitleFromProjectWrapper>
            }
          />
        </Route>
      </Route>
    </Routes>
  )
}

const App = () => {
  useIntercom()

  return (
    <>
      {appConfig.showDeploymentInfo && <PreviewDeploymentWarningDialog apiURL={appConfig.apiURL} />}
      <AuthRequiredRoutes />
    </>
  )
}

export default App
